import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { EyeIcon, EyeSlashIcon, TagIcon } from '@heroicons/react/24/outline';
import { Form, Button, Alert } from 'react-bootstrap';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationType, setVerificationType] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(formData.username, formData.password);

    setLoading(false);

    if (result.success) {
      navigate('/');
    } else if (result.requiresVerification) {
      setNeedsVerification(true);
      setVerificationType(result.verificationType || '');
      setError(result.message);
    } else {
      setError(result.message);
    }
  };

  return (
    <Layout showSidebar={false}>
      <div className="d-flex align-items-center justify-content-center px-4 px-md-0 pw-page" style={{ minHeight: '80vh' }}>
        <div className="w-100" style={{ maxWidth: '448px' }}>
          {/* Mobile Header */}
          <div className="d-md-none text-center mb-4">
            <div className="mx-auto mb-3 rounded-4 d-flex align-items-center justify-content-center shadow-primary" style={{ width: '64px', height: '64px', background: 'linear-gradient(135deg, #0d6efd, #084298)' }}>
              <TagIcon className="text-white" style={{ width: '32px', height: '32px' }} />
            </div>
            <h1 className="fw-bold text-dark fs-4">Welcome Back</h1>
            <p className="text-secondary mt-1">Sign in to continue</p>
          </div>

          {/* Desktop Header */}
          <div className="d-none d-md-block text-center mb-4">
            <h1 className="fw-bold text-dark fs-2 mb-2">Welcome Back</h1>
            <p className="text-secondary">Sign in to your NayaBajar account</p>
          </div>

          <div className="bg-white rounded-4 border p-4 p-md-5 d-none d-md-block pw-card">
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
                {needsVerification && (
                  <div className="mt-2">
                    <Link
                      to={`/verify?type=${verificationType}&email=${formData.username}`}
                      className="text-primary fw-medium text-decoration-none"
                    >
                      Click here to verify
                    </Link>
                  </div>
                )}
              </Alert>
            )}

            <Form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
              {/* Email/Username */}
              <Form.Group>
                <Form.Label className="fw-medium text-secondary small">
                  Email or Username
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="px-4 py-2 border rounded-3"
                  placeholder="Enter your email or username"
                  required
                />
              </Form.Group>

              {/* Password */}
              <Form.Group>
                <Form.Label className="fw-medium text-secondary small">
                  Password
                </Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pe-5 px-4 py-2 border rounded-3"
                    placeholder="Enter your password"
                    required
                  />
                  <Button
                    variant="link"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="position-absolute top-50 end-0 translate-middle-y pe-3 text-secondary"
                    style={{ border: 'none', background: 'none' }}
                  >
                    {showPassword ? (
                      <EyeSlashIcon style={{ width: '20px', height: '20px' }} />
                    ) : (
                      <EyeIcon style={{ width: '20px', height: '20px' }} />
                    )}
                  </Button>
                </div>
              </Form.Group>

              {/* Remember & Forgot */}
              <div className="d-flex align-items-center justify-content-between small">
                <Form.Check
                  type="checkbox"
                  label={<span className="text-secondary">Remember me</span>}
                  className="user-select-none"
                />
                <Link to="/forgot-password" className="text-primary fw-medium text-decoration-none">
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button - Primary Blue */}
              <Button
                type="submit"
                disabled={loading}
                variant="primary"
                className="w-100 py-2 fw-semibold"
                style={{ opacity: loading ? 0.5 : 1 }}
              >
                {loading ? (
                  <span className="d-flex align-items-center justify-content-center">
                    <svg className="animate-spin me-2" style={{ width: '20px', height: '20px', color: 'white' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </Button>
            </Form>

            {/* Divider */}
            <div className="position-relative my-4">
              <div className="position-absolute top-50 start-0 end-0 border-top"></div>
              <div className="position-absolute top-50 start-50 translate-middle">
                <span className="bg-white text-secondary px-2" style={{ fontSize: '14px' }}>or</span>
              </div>
            </div>

            {/* Register Link */}
            <div className="text-center">
              <p className="text-secondary">
                Don't have an account?{' '}
                <Link to="/register" className="text-primary fw-semibold text-decoration-none">
                  Create Account
                </Link>
              </p>
            </div>
          </div>

          {/* Mobile Form */}
          <div className="bg-white p-4 d-md-none pw-card">
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
                {needsVerification && (
                  <div className="mt-2">
                    <Link
                      to={`/verify?type=${verificationType}&email=${formData.username}`}
                      className="text-primary fw-medium text-decoration-none"
                    >
                      Click here to verify
                    </Link>
                  </div>
                )}
              </Alert>
            )}

            <Form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
              {/* Email/Username */}
              <Form.Group>
                <Form.Label className="fw-medium text-secondary small">
                  Email or Username
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="px-4 py-2 border rounded-3"
                  placeholder="Enter your email or username"
                  required
                />
              </Form.Group>

              {/* Password */}
              <Form.Group>
                <Form.Label className="fw-medium text-secondary small">
                  Password
                </Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pe-5 px-4 py-2 border rounded-3"
                    placeholder="Enter your password"
                    required
                  />
                  <Button
                    variant="link"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="position-absolute top-50 end-0 translate-middle-y pe-3 text-secondary"
                    style={{ border: 'none', background: 'none' }}
                  >
                    {showPassword ? (
                      <EyeSlashIcon style={{ width: '20px', height: '20px' }} />
                    ) : (
                      <EyeIcon style={{ width: '20px', height: '20px' }} />
                    )}
                  </Button>
                </div>
              </Form.Group>

              {/* Remember & Forgot */}
              <div className="d-flex align-items-center justify-content-between small">
                <Form.Check
                  type="checkbox"
                  label={<span className="text-secondary">Remember me</span>}
                  className="user-select-none"
                />
                <Link to="/forgot-password" className="text-primary fw-medium text-decoration-none">
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button - Primary Blue */}
              <Button
                type="submit"
                disabled={loading}
                variant="primary"
                className="w-100 py-2 fw-semibold"
                style={{ opacity: loading ? 0.5 : 1 }}
              >
                {loading ? (
                  <span className="d-flex align-items-center justify-content-center">
                    <svg className="animate-spin me-2" style={{ width: '20px', height: '20px', color: 'white' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </Button>
            </Form>

            {/* Divider */}
            <div className="position-relative my-4">
              <div className="position-absolute top-50 start-0 end-0 border-top"></div>
              <div className="position-absolute top-50 start-50 translate-middle">
                <span className="bg-white text-secondary px-2" style={{ fontSize: '14px' }}>or</span>
              </div>
            </div>

            {/* Register Link */}
            <div className="text-center">
              <p className="text-secondary">
                Don't have an account?{' '}
                <Link to="/register" className="text-primary fw-semibold text-decoration-none">
                  Create Account
                </Link>
              </p>
            </div>
          </div>

          {/* Demo Credentials - Mobile Only */}
          <div className="d-md-none mt-4 p-3 rounded-3 text-center border" style={{ backgroundColor: 'rgba(13, 110, 253, 0.05)', borderColor: 'rgba(13, 110, 253, 0.1)' }}>
            <p className="text-primary fw-medium mb-2" style={{ fontSize: '12px' }}>Demo Credentials</p>
            <p className="text-secondary" style={{ fontSize: '14px' }}>admin@nayabajar.com / admin123</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Login;
